// src/client.ts
var ApiClient = class {
  baseUrl;
  getAccessToken;
  onTokenRefresh;
  onAuthError;
  constructor(config) {
    this.baseUrl = config.baseUrl.replace(/\/$/, "");
    this.getAccessToken = config.getAccessToken ?? (() => null);
    this.onTokenRefresh = config.onTokenRefresh;
    this.onAuthError = config.onAuthError;
  }
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getAccessToken();
    const headers = {
      "Content-Type": "application/json",
      ...options.headers
    };
    if (token) {
      ;
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(url, {
      ...options,
      headers
    });
    if (!response.ok) {
      if (response.status === 401) {
        this.onAuthError?.();
      }
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {};
      }
      const error = {
        code: errorData.code ?? "UNKNOWN_ERROR",
        message: errorData.message ?? "An unknown error occurred",
        status: response.status
      };
      throw error;
    }
    return response.json();
  }
  async requestFormData(endpoint, formData) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getAccessToken();
    const headers = {};
    if (token) {
      ;
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData
    });
    if (!response.ok) {
      if (response.status === 401) {
        this.onAuthError?.();
      }
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {};
      }
      const error = {
        code: errorData.code ?? "UNKNOWN_ERROR",
        message: errorData.message ?? "An unknown error occurred",
        status: response.status
      };
      throw error;
    }
    return response.json();
  }
  // ============ Auth ============
  async signUp(data) {
    return this.request("/auth/signup", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }
  async signIn(data) {
    return this.request("/auth/signin", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }
  async signOut() {
    await this.request("/auth/signout", {
      method: "POST"
    });
  }
  async getCurrentUser() {
    return this.request("/auth/me");
  }
  async refreshToken(refreshToken) {
    const response = await this.request("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refreshToken })
    });
    this.onTokenRefresh?.(response);
    return response;
  }
  async forgotPassword(email) {
    await this.request("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email })
    });
  }
  // ============ Scripts ============
  async getScripts() {
    return this.request("/scripts");
  }
  async getScript(id) {
    return this.request(`/scripts/${id}`);
  }
  async createScript(data) {
    return this.request("/scripts", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }
  async updateScript(id, data) {
    return this.request(`/scripts/${id}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }
  async deleteScript(id) {
    await this.request(`/scripts/${id}`, {
      method: "DELETE"
    });
  }
  // ============ Transcription ============
  async transcribe(data) {
    const formData = new FormData();
    const audioFile = new File([data.audioBlob], "audio.webm", {
      type: data.audioBlob.type
    });
    formData.append("file", audioFile);
    if (data.language) {
      formData.append("language", data.language);
    }
    if (data.prompt) {
      formData.append("prompt", data.prompt);
    }
    return this.requestFormData("/transcription", formData);
  }
  // ============ Subscription ============
  async getSubscription() {
    return this.request("/subscription");
  }
  async getUsageSummary() {
    return this.request("/subscription/usage");
  }
  async createCheckoutSession(plan) {
    return this.request("/subscription/checkout", {
      method: "POST",
      body: JSON.stringify({ plan })
    });
  }
  async createPortalSession() {
    return this.request("/subscription/portal", {
      method: "POST"
    });
  }
  async cancelSubscription() {
    await this.request("/subscription/cancel", {
      method: "POST"
    });
  }
  async requestRefund(reason) {
    return this.request("/subscription/refund", {
      method: "POST",
      body: JSON.stringify({ reason })
    });
  }
  // ============ Admin ============
  async adminUpdateUserPlan(userId, plan, whisperMinutesLimit) {
    return this.request(`/admin/users/${userId}/plan`, {
      method: "PATCH",
      body: JSON.stringify({ plan, whisperMinutesLimit })
    });
  }
};
var createApiClient = (config) => new ApiClient(config);

// src/whisper.ts
var WHISPER_API_URL = "https://api.openai.com/v1/audio/transcriptions";
var parseWhisperError = (status, errorData) => {
  if (status === 401) {
    return { type: "auth", message: "API \uD0A4\uAC00 \uC720\uD6A8\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4", status };
  }
  if (status === 429) {
    return { type: "rate_limit", message: "API \uC694\uCCAD \uD55C\uB3C4 \uCD08\uACFC", status };
  }
  if (status === 413) {
    return { type: "file_size", message: "\uB179\uC74C \uD30C\uC77C\uC774 \uB108\uBB34 \uD07D\uB2C8\uB2E4 (\uCD5C\uB300 25MB)", status };
  }
  const errorMessage = errorData && typeof errorData === "object" && "error" in errorData ? errorData.error?.message || "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958" : "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958";
  return { type: "unknown", message: errorMessage, status };
};
var transcribeAudioDirect = async ({
  apiKey,
  audioBlob,
  language = "ko",
  prompt
}) => {
  if (!apiKey) {
    throw { type: "auth", message: "API \uD0A4\uAC00 \uD544\uC694\uD569\uB2C8\uB2E4" };
  }
  if (audioBlob.size > 25 * 1024 * 1024) {
    throw { type: "file_size", message: "\uB179\uC74C \uD30C\uC77C\uC774 \uB108\uBB34 \uD07D\uB2C8\uB2E4 (\uCD5C\uB300 25MB)" };
  }
  const formData = new FormData();
  const audioFile = new File([audioBlob], "audio.webm", { type: audioBlob.type });
  formData.append("file", audioFile);
  formData.append("model", "whisper-1");
  formData.append("language", language);
  formData.append("response_format", "verbose_json");
  if (prompt) {
    const truncatedPrompt = prompt.slice(0, 500);
    formData.append("prompt", truncatedPrompt);
  }
  try {
    const response = await fetch(WHISPER_API_URL, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${apiKey}`
      },
      body: formData
    });
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = null;
      }
      throw parseWhisperError(response.status, errorData);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    if (error && typeof error === "object" && "type" in error) {
      throw error;
    }
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw { type: "network", message: "\uB124\uD2B8\uC6CC\uD06C \uC5F0\uACB0\uC744 \uD655\uC778\uD574\uC8FC\uC138\uC694" };
    }
    throw { type: "unknown", message: "\uC54C \uC218 \uC5C6\uB294 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4" };
  }
};
var validateApiKey = (apiKey) => {
  return apiKey.startsWith("sk-") && apiKey.length > 20;
};
export {
  ApiClient,
  createApiClient,
  transcribeAudioDirect,
  validateApiKey
};
//# sourceMappingURL=index.js.map